import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';
import { formatEUR, formatDate } from '../lib/format';
import { cn } from '../utils/cn';
import { InvoiceModal } from '../components/collection/InvoiceModal';
import { RecordPaymentDialog } from '../components/collection/RecordPaymentDialog';
import {
  Tile,
  Banner,
  Empty,
  StatusPill,
  labelCls,
  thCls,
  thNum,
  tdCls,
  tdNum,
  monthLabel,
  type Inv,
} from '../components/collection/collectionShared';

/**
 * Factures récapitulatives (owner's Recouvrement prototype, 2026-10-16): one invoice per garage
 * and per month, carrying its BLs — what is settled, what is at the bank, what is left. Click an
 * invoice for its payments and the letters that named it. The status is never set by hand: it
 * follows the money recorded on the collection screens.
 */

interface Data {
  months: string[];
  customers: Array<{ id: string; name: string }>;
  counts: { all: number; unpaid: number; paid: number };
  zero: string[];
  totals: {
    count: number;
    paidCount: number;
    invoiced: number;
    settled: number;
    inCollection: number;
    remaining: number;
    overdue: number;
  };
  list: Array<Inv & { month: string; paidBy: string[] }>;
}

export default function RecapTracking() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [data, setData] = useState<Data | null>(null);
  const [month, setMonth] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [pay, setPay] = useState<{ customerId: string; invoiceId: string } | null>(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams();
    if (month) qs.set('month', month);
    if (customerId) qs.set('customerId', customerId);
    if (status) qs.set('status', status);
    apiClient
      .get<Data>(`/collection/recaps?${qs}`)
      .then((r) => setData(r.data))
      .catch((e) => showApiError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, customerId, status]);
  useEffect(load, [load]);

  const T = data?.totals;
  const seg = (v: string, label: string, n: number | undefined) => (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm',
        status === v && 'border-primary bg-primary-soft font-semibold'
      )}
    >
      <input
        type="radio"
        name="recap-status"
        className="h-4 w-4"
        checked={status === v}
        onChange={() => setStatus(v)}
      />
      {label}
      <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">{n ?? 0}</span>
    </label>
  );

  return (
    <div>
      <PageHeader
        title={t('collection.recaps.title')}
        description={t('collection.recaps.description')}
      />
      {data && data.zero.length > 0 && (
        <Banner tone="bad">{t('collection.recaps.zero', { numbers: data.zero.join(', ') })}</Banner>
      )}
      {T && (
        <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            label={t('collection.recaps.invoiced')}
            value={formatEUR(T.invoiced)}
            sub={t('collection.recaps.invoicedSub', { count: T.count })}
          />
          <Tile
            label={t('collection.recaps.settled')}
            value={formatEUR(T.settled)}
            sub={t('collection.recaps.settledSub', { count: T.paidCount })}
          />
          <Tile
            label={t('collection.recaps.remaining')}
            value={formatEUR(T.remaining)}
            alert={T.overdue > 0}
            sub={t('collection.recaps.remainingSub', { amount: formatEUR(T.overdue) })}
          />
        </div>
      )}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex flex-wrap items-end gap-3 border-b p-3">
          <label className="block min-w-[160px]">
            <span className={labelCls}>{t('collection.recaps.month')}</span>
            <Select value={month} onChange={(e) => setMonth(e.target.value)}>
              <option value="">{t('collection.common.all')}</option>
              {(data?.months ?? []).map((m) => (
                <option key={m} value={m}>
                  {monthLabel(t, m)}
                </option>
              ))}
            </Select>
          </label>
          <label className="block min-w-[200px]">
            <span className={labelCls}>{t('collection.recaps.garage')}</span>
            <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
              <option value="">{t('collection.common.all')}</option>
              {(data?.customers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </label>
          <div className="flex-1">
            <span className={labelCls}>{t('collection.recaps.show')}</span>
            <div className="flex flex-wrap gap-2">
              {seg('', t('collection.common.all'), data?.counts.all)}
              {seg('NON', t('collection.recaps.unpaid'), data?.counts.unpaid)}
              {seg('REGLEE', t('collection.recaps.paid'), data?.counts.paid)}
            </div>
          </div>
        </div>
        {data === null ? null : data.list.length === 0 ? (
          <Empty>{t('collection.recaps.empty')}</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className={thCls}>{t('collection.col.invoice')}</th>
                  <th className={thCls}>{t('collection.recaps.garage')}</th>
                  <th className={thCls}>{t('collection.recaps.month')}</th>
                  <th className={thNum}>{t('collection.col.amount')}</th>
                  <th className={thNum}>{t('collection.col.settled')}</th>
                  <th className={thNum}>{t('collection.col.remaining')}</th>
                  <th className={thCls}>{t('collection.recaps.paidBy')}</th>
                  <th className={thCls}>{t('collection.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.list.map((x) => (
                  <tr
                    key={x.id}
                    className="cursor-pointer border-b last:border-b-0 hover:bg-muted/50"
                    onClick={() => setOpen(x.id)}
                  >
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{x.number}</span>
                      <div className="text-xs text-muted-foreground">
                        {t('collection.overview.bls', { count: x.deliveryNotes })} ·{' '}
                        {t('collection.overview.dueOn', { date: formatDate(x.dueDate) })}
                      </div>
                    </td>
                    <td className={tdCls}>
                      {x.customer?.name}
                      <div className="text-xs text-muted-foreground">
                        {x.customer?.type === 'B2B_ACCOUNT'
                          ? (x.customer.terms ?? '')
                          : t('collection.recaps.paidAtOrder')}
                      </div>
                    </td>
                    <td className={tdCls}>{monthLabel(t, x.month)}</td>
                    <td className={tdNum}>{formatEUR(x.total)}</td>
                    <td className={tdNum}>
                      {formatEUR(x.s.paid)}
                      {x.s.pending > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {t('collection.overview.pendingAmt', {
                            amount: formatEUR(Math.min(x.s.pending, x.s.remaining)),
                          })}
                        </div>
                      )}
                    </td>
                    <td className={tdNum}>
                      <b>{formatEUR(x.s.remaining)}</b>
                    </td>
                    <td className={tdCls}>
                      {x.paidBy
                        .map((m) => t(`collection.mode.${m}`, { defaultValue: m }))
                        .join(', ') || '—'}
                    </td>
                    <td className={tdCls}>
                      <StatusPill s={x.s} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {open && (
        <InvoiceModal
          invoiceId={open}
          onClose={() => setOpen(null)}
          onPay={(c, i) => {
            setOpen(null);
            setPay({ customerId: c, invoiceId: i });
          }}
        />
      )}
      {pay && (
        <RecordPaymentDialog
          customerId={pay.customerId}
          invoiceId={pay.invoiceId}
          onClose={() => setPay(null)}
          onDone={load}
        />
      )}
    </div>
  );
}
